import React, { useCallback, useEffect, useMemo, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { DataGrid } from "@mui/x-data-grid";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import RefreshIcon from "@mui/icons-material/Refresh";

const widthByHeader = {
	Scanner: 95,
	Date: 130,
	Program: 120,
	Operator: 130,
	File: 110,
	"Client Name": 190,
	Department: 160,
	Processed: 140,
	Notes: 260,
	Note: 260,
};

function getErrorMessage(error) {
	if (error && error.message) {
		return error.message;
	}
	return "Unable to load the exam log.";
}

export default function ExamLogSheetPanel() {
	const [sheetData, setSheetData] = useState(null);
	const [selectedSheet, setSelectedSheet] = useState("");
	const [loading, setLoading] = useState(false);
	const [savingCell, setSavingCell] = useState("");
	const [error, setError] = useState("");
	const [message, setMessage] = useState("");

	const token = sessionStorage.getItem("token");
	const backendHost = process.env.REACT_APP_BACKEND_HOST;

	const loadSheet = useCallback(
		async (sheetName = selectedSheet) => {
			setLoading(true);
			setError("");
			setMessage("");

			try {
				const queryString = sheetName ? `?sheet_name=${encodeURIComponent(sheetName)}` : "";
				const response = await fetch(`${backendHost}/scanner/exam-log/${queryString}`, {
					headers: {
						Accept: "application/json",
						Authorization: `Token ${token}`,
					},
				});
				const data = await response.json();

				if (!response.ok) {
					throw new Error(data.error || "Unable to load the exam log.");
				}

				setSheetData(data);
				if (!selectedSheet) {
					setSelectedSheet(data.sheet_name);
				}
			} catch (loadError) {
				setError(getErrorMessage(loadError));
			} finally {
				setLoading(false);
			}
		},
		[backendHost, selectedSheet, token]
	);

	useEffect(() => {
		if (!sheetData) {
			loadSheet();
		}
	}, [loadSheet, sheetData]);

	const columnMetadata = useMemo(() => {
		if (!sheetData) {
			return [];
		}

		return sheetData.headers.map((header, index) => ({
			field: `column_${index}`,
			header,
		}));
	}, [sheetData]);

	const columns = useMemo(
		() =>
			columnMetadata.map(({ field, header }) => ({
				field,
				headerName: header,
				width: widthByHeader[header] || 140,
				editable: true,
			})),
		[columnMetadata]
	);

	const rows = useMemo(() => {
		if (!sheetData) {
			return [];
		}

		return sheetData.rows.map((row) => {
			const mappedRow = {
				id: row.row_number,
				row_number: row.row_number,
			};
			columnMetadata.forEach(({ field, header }) => {
				mappedRow[field] = row.values[header] ?? "";
			});
			return mappedRow;
		});
	}, [columnMetadata, sheetData]);

	const handleSheetChange = (event) => {
		const nextSheet = event.target.value;
		setSelectedSheet(nextSheet);
		loadSheet(nextSheet);
	};

	const handleProcessRowUpdate = async (newRow, oldRow) => {
		const column = columnMetadata.find(
			(item) => String(newRow[item.field] ?? "") !== String(oldRow[item.field] ?? ""),
		);
		if (!column) {
			return oldRow;
		}

		const nextValue = newRow[column.field] ?? "";

		const cellKey = `${newRow.id}-${column.field}`;
		setSavingCell(cellKey);
		setError("");
		setMessage("");

		try {
			const response = await fetch(`${backendHost}/scanner/exam-log/cell/`, {
				method: "PATCH",
				headers: {
					"Content-Type": "application/json",
					Accept: "application/json",
					Authorization: `Token ${token}`,
				},
				body: JSON.stringify({
					sheet_name: sheetData.sheet_name,
					row_number: newRow.id,
					column: column.header,
					value: nextValue,
				}),
			});
			const data = await response.json();

			if (!response.ok) {
				throw new Error(data.error || "Unable to save the cell.");
			}

			setMessage(`Saved ${data.cell}`);
			setSheetData((previous) => ({
				...previous,
				rows: previous.rows.map((row) => {
					if (row.row_number !== newRow.id) {
						return row;
					}
					return {
						...row,
						values: {
							...row.values,
							[column.header]: data.updated_value,
						},
					};
				}),
			}));
			return {
				...newRow,
				[column.field]: data.updated_value,
			};
		} catch (saveError) {
			setError(getErrorMessage(saveError));
			loadSheet(sheetData.sheet_name);
			throw saveError;
		} finally {
			setSavingCell("");
		}
	};

	return (
		<Stack spacing={2} sx={{ height: "100%", width: "100%" }}>
			<Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
				<Stack direction="row" alignItems="center" spacing={2}>
					<FormControl size="small" sx={{ minWidth: 180 }}>
						<InputLabel id="exam-log-sheet-label">Sheet</InputLabel>
						<Select
							labelId="exam-log-sheet-label"
							label="Sheet"
							value={selectedSheet}
							onChange={handleSheetChange}
							disabled={!sheetData || loading}
						>
							{(sheetData?.available_sheets || [selectedSheet]).map((sheetName) => (
								<MenuItem key={sheetName} value={sheetName}>
									{sheetName}
								</MenuItem>
							))}
						</Select>
					</FormControl>
					<Button
						variant="outlined"
						startIcon={<RefreshIcon />}
						onClick={() => loadSheet()}
						disabled={loading}
					>
						Refresh
					</Button>
					{sheetData?.spreadsheet_url && (
						<Button
							variant="outlined"
							startIcon={<OpenInNewIcon />}
							href={sheetData.spreadsheet_url}
							target="_blank"
							rel="noreferrer"
						>
							Open
						</Button>
					)}
				</Stack>
				<Stack direction="row" alignItems="center" spacing={1}>
					{savingCell && <CircularProgress size={20} />}
					<Typography variant="body2" color="text.secondary">
						{sheetData ? `${sheetData.rows.length} rows` : ""}
					</Typography>
				</Stack>
			</Stack>

			{error && <Alert severity="error">{error}</Alert>}
			{message && <Alert severity="success">{message}</Alert>}

			<Box sx={{ flex: 1, minHeight: 0, width: "100%" }}>
				<DataGrid
					rows={rows}
					columns={columns}
					loading={loading}
					initialState={{
						pagination: {
							paginationModel: { page: 0, pageSize: 25 },
						},
					}}
					pageSizeOptions={[25, 50, 100]}
					disableRowSelectionOnClick
					processRowUpdate={handleProcessRowUpdate}
					onProcessRowUpdateError={(updateError) => setError(getErrorMessage(updateError))}
				/>
			</Box>
		</Stack>
	);
}
